'use client';

import { useEffect } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';

/**
 * Google Analytics hook for automatic page view tracking
 * Tracks page views when pathname or search params change
 */
export function useGoogleAnalytics() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    const measurementId = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID;
    if (!measurementId) return;

    // Initialize gtag if not already present
    if (!window.dataLayer) {
      const script = document.createElement('script');
      script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
      script.async = true;
      document.head.appendChild(script);

      window.dataLayer = window.dataLayer || [];
      function gtag(...args: any[]) {
        window.dataLayer.push(args);
      }
      window.gtag = gtag;

      gtag('js', new Date());
      gtag('config', measurementId, {
        page_path: pathname,
      });
    }

    // Track page view
    const url = pathname + (searchParams.toString() ? `?${searchParams}` : '');
    if (window.gtag) {
      window.gtag('event', 'page_view', {
        page_path: url,
      });
    }
  }, [pathname, searchParams]);
}

/**
 * Track custom events in Google Analytics
 * @param eventName - Name of the event (e.g., 'quote_request', 'catalog_view')
 * @param eventParams - Additional parameters for the event
 */
export function trackEvent(eventName: string, eventParams: Record<string, any> = {}) {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, eventParams);
  }
}

/**
 * Track quote request events with source attribution
 */
export function trackQuoteRequest(params: {
  model_name: string;
  model_id: string;
  category?: string;
  company: string;
  country: string;
  source: string; // 'catalog', 'product_page', 'homepage'
}) {
  trackEvent('quote_request', {
    event_category: 'conversion',
    event_label: params.model_name,
    model_id: params.model_id,
    category: params.category || 'unknown',
    company: params.company,
    country: params.country,
    source: params.source,
    value: 1, // For conversion tracking
  });
}

/**
 * Track catalog performance - product views
 */
export function trackProductView(params: {
  model_name: string;
  model_id: string;
  category: string;
}) {
  trackEvent('product_view', {
    event_category: 'catalog',
    event_label: params.model_name,
    model_id: params.model_id,
    category: params.category,
  });
}

/**
 * Track category navigation
 */
export function trackCategoryView(params: {
  category: string;
  subcategory?: string;
}) {
  trackEvent('category_view', {
    event_category: 'catalog',
    event_label: params.category,
    subcategory: params.subcategory || 'all',
  });
}

/**
 * Track distributor engagement actions
 */
export function trackDistributorEngagement(action: string, params: Record<string, any> = {}) {
  trackEvent('distributor_engagement', {
    event_category: 'engagement',
    event_label: action,
    ...params,
  });
}

/**
 * Track contact actions (WhatsApp, WeChat, Email)
 */
export function trackContactClick(contactType: 'whatsapp' | 'wechat' | 'email' | 'phone', source: string) {
  trackEvent('contact_click', {
    event_category: 'engagement',
    event_label: contactType,
    source: source,
  });
}

/**
 * Track PDF downloads
 */
export function trackPDFDownload(params: {
  file_name: string;
  model_name?: string;
  type: 'datasheet' | 'certificate' | 'brochure';
}) {
  trackEvent('file_download', {
    event_category: 'engagement',
    event_label: params.file_name,
    model_name: params.model_name || 'general',
    file_type: params.type,
  });
}

/**
 * Track form submissions
 */
export function trackFormSubmission(formType: string, params: Record<string, any> = {}) {
  trackEvent('form_submission', {
    event_category: 'conversion',
    event_label: formType,
    ...params,
  });
}

// TypeScript declarations for gtag
declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}